import React, { createRef } from "react";
import LazyImage from "./LazyImage";
import { absoluteURL } from "../../../../util/UrlHelper";

/**
 * <ImageCrop src=".." (heightRatio={0.5}) (widthRatio={0.5}) (offset={{x: 0.5, y: 0.0}})
 */

interface ImageCropProps
{
    src: string;
    alt?: string;

    /**
     * The top-left corner of the cropped image, specified in the original
     * image's coordinate space.
     */
    offset?: {
        /**
         * Decimal value between 0 and 1
         */
        x: number;

        /**
        * Decimal value between 0 and 1
        */
        y: number;
    };

    /**
     * Set the max width. Use string for percentage
     */
    width?: number | string;

    /**
     * Sets the max height. Use string for percentage
     */
    height?: number | string;

    /**
     * Set the decimal ratio of width relative to the height. (Only works when height is explicitedly)
     */
    widthRatio?: number;

    /**
     * Set the decimal ratio of the height relative to the width.
     */
    heightRatio?: number;

    /**
     * (Optional) the resizing mode to use when scaling the image. Defaults to "max"
     */
    resizeMode?: "pad" | "min" | "max" | "stretch" | "crop" | "boxpad" | "strecth";

    /**
     * Background color when using boxpad
     */
    bgcolor?: string;
}

interface ImageCropStateProps
{
    calcWidth?: number;
    calcHeight?: number;
    imgSrc?: string;
}

class ImageCrop extends React.Component<ImageCropProps & React.ImgHTMLAttributes<{}>, ImageCropStateProps>
{
    _isMounted = false;
    timeoutHandleId = 0;
    state = {
        calcWidth: undefined,
        calcHeight: undefined,
        imgSrc: undefined
    }

    private lazyImageElementRef: React.RefObject<HTMLImageElement>;

    constructor(props: ImageCropProps)
    {
        super(props);

        this.lazyImageElementRef = createRef<HTMLImageElement>();
    }

    private getPercentage(n?: string | number)
    {
        const s = n?.toString();
        if (s?.endsWith("%"))
        {
            return parseInt(s) / 100;
        }
        return null;
    }

    private appendWidth(params: URLSearchParams, calcWidth)
    {
        const { width, widthRatio } = this.props;
        // Use width, if explicitly defined
        if (width && typeof width === "number")
        {
            params.set("width", String(width));
        }
        else if (widthRatio)
        {
            params.set("widthratio", String(widthRatio));
        }
        // If none of the above is defined, use calculated width
        else if (calcWidth)
        {
            params.set("width", String(calcWidth));
        }
    }

    private appendHeight(params: URLSearchParams, calcHeight)
    {
        const { height, heightRatio } = this.props;
        if (height && typeof height === "number")
        {
            params.set("height", String(height));
        }
        else if (heightRatio)
        {
            params.set("heightratio", String(heightRatio));
        }
        else if (calcHeight)
        {
            params.set("height", String(calcHeight));
        }
    }

    private getURL(calcHeight, calcWidth)
    {
        const { src: originalSrc, offset, resizeMode, bgcolor } = this.props;
        const newImageSrc = new URL(absoluteURL(originalSrc));
        const params = newImageSrc.searchParams;

        this.appendWidth(params, calcWidth);

        this.appendHeight(params, calcHeight);

        if (offset && offset.x && offset.y)
        {
            const center = [offset.y, offset.x].join(",");
            params.set("center", center);
        }

        if (resizeMode)
        {
            params.set("mode", resizeMode);
        }
        else
        {
            params.set("mode", "max");
        }

        if (bgcolor)
        {
            params.set("bgcolor", bgcolor);
        }
        return `${newImageSrc}`;
    }

    private handleResize()
    {
        if (this.props)
        {
            const { src, width, widthRatio, height, heightRatio } = this.props;

            if (!src)
            {
                return;
            }

            const { calcWidth, calcHeight } = this.state;

            const current = this.lazyImageElementRef.current;
            const parentElement = current?.parentElement;

            let setCalcHeight: undefined | number;
            let setCalcWidth: undefined | number;

            if (parentElement)
            {
                const boundingClientRect = parentElement.getBoundingClientRect();
                const offsetParentWidth = boundingClientRect?.width || 0;
                const offsetParentHeight = boundingClientRect?.height || 0;
                // Calc width by percetage
                if (offsetParentWidth > 0)
                {
                    const widthPercentage = this.getPercentage(width);
                    if (widthPercentage)
                    {
                        setCalcWidth = (offsetParentWidth * widthPercentage);
                    }
                    else if (!height && !widthRatio)
                    {
                        setCalcWidth = (offsetParentWidth);
                    }
                }

                // Calc height by percetage
                if (offsetParentHeight > 0)
                {
                    const heightPercentage = this.getPercentage(height);
                    if (heightPercentage)
                    {
                        setCalcHeight = (offsetParentHeight * heightPercentage);
                    }
                    else if (!width && !heightRatio)
                    {
                        setCalcHeight = (offsetParentHeight);
                    }
                }
            }

            // Calc width by calc height * width ratio
            if (setCalcHeight && typeof calcHeight === "number" && widthRatio)
            {
                setCalcWidth = (setCalcHeight * widthRatio);
            }

            // Calc height using by calc width * height ratio
            if (setCalcWidth && typeof calcWidth === "number" && heightRatio)
            {
                setCalcHeight = (setCalcWidth * heightRatio);
            }

            const url = this.getURL(setCalcHeight, setCalcWidth);
            if (this._isMounted)
            {
                if (url !== this.state.imgSrc)
                {
                    this.setState({
                        imgSrc: url,
                        calcHeight: setCalcHeight,
                        calcWidth: setCalcWidth
                    });
                }
            }
        }
    }

    handleResizeTimer()
    {
        clearTimeout(this.timeoutHandleId);
        this.timeoutHandleId = window.setTimeout(this.handleResize, 500);
    }

    componentDidMount()
    {
        this._isMounted = true;
        if (this.lazyImageElementRef.current)
        {
            window.addEventListener("resize", this.handleResizeTimer);
            this.handleResize();
        }
    }

    componentWillUnmount()
    {
        this._isMounted = false;
        window.removeEventListener("resize", this.handleResizeTimer);
    }

    componentDidUpdate()
    {
        this.handleResize();
    }

    render()
    {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { src: originalSrc, height, heightRatio, width, widthRatio, offset, resizeMode, bgcolor, ...rest } = this.props;

        return <LazyImage src={this.state.imgSrc} forwardedRef={this.lazyImageElementRef} {...rest} />;
    }
}

export default ImageCrop;
